import React, { useEffect, useState } from 'react';
import { Edit2, Save, X, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { updateRestaurant } from '../../api/restaurantsApi';

const LeadsEmailSection = ({ locationId, leadsEmail, onSaved }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedEmails, setSavedEmails] = useState(Array.isArray(leadsEmail) ? leadsEmail : []);
  const [recipients, setRecipients] = useState(savedEmails);
  const [error, setError] = useState('');

  useEffect(() => {
    setSavedEmails(Array.isArray(leadsEmail) ? leadsEmail : []);
  }, [leadsEmail]);

  const handleSave = async (event) => {
    event.preventDefault();
    setError('');
    const emails = recipients.map(email => email.trim());
    const invalidIndex = emails.findIndex(email => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
    if (invalidIndex !== -1 || !event.currentTarget.checkValidity()) {
      setError(invalidIndex === -1 ? 'Enter a valid email address for every recipient.' : `Enter a valid email address for recipient ${invalidIndex + 1}.`);
      return;
    }
    const uniqueEmails = emails.filter((email, index) => emails.findIndex(other => other.toLowerCase() === email.toLowerCase()) === index);
    setIsSaving(true);
    try {
      const updated = await updateRestaurant(locationId, { leadsEmail: uniqueEmails });
      setSavedEmails(Array.isArray(updated?.leadsEmail) ? updated.leadsEmail : uniqueEmails);
      setIsEditing(false);
    } catch (saveError) {
      setError(saveError.message || 'Could not save lead email recipients. Please try again.');
      setIsSaving(false);
      return;
    }
    try {
      await onSaved?.();
    } catch (refreshError) {
      setError('Lead email recipients saved. Reload the page to see the latest settings.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="bg-gray-800 border-gray-700">
      <form onSubmit={handleSave} noValidate>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-white">Leads Email</h3>
              <p className="text-sm text-gray-400">Email addresses that receive call lead summaries</p>
            </div>
            {isEditing ? (
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" disabled={isSaving}
                  onClick={() => { setIsEditing(false); setRecipients(savedEmails); setError(''); }}>
                  <X className="w-4 h-4 mr-2" /> Cancel
                </Button>
                <Button type="submit" size="sm" disabled={isSaving}>
                  <Save className="w-4 h-4 mr-2" /> {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            ) : (
              <Button type="button" size="sm" disabled={!locationId || isSaving}
                onClick={() => { setRecipients(savedEmails); setError(''); setIsEditing(true); }}>
                <Edit2 className="w-4 h-4 mr-2" /> Edit
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {isEditing ? (
            <>
              {recipients.map((email, index) => (
                <div key={index} className="flex items-end gap-2">
                  <div className="min-w-0 flex-1">
                    <label htmlFor={`lead-email-${index}`} className="block text-sm font-medium text-gray-300 mb-2">
                      Email address {index + 1}
                    </label>
                    <Input id={`lead-email-${index}`} type="email" required maxLength={254}
                      value={email} disabled={isSaving} placeholder="name@company.com"
                      aria-describedby={error ? 'leads-email-error' : undefined}
                      onChange={event => setRecipients(current => current.map((value, row) => row === index ? event.target.value : value))} />
                  </div>
                  <Button type="button" variant="outline" size="icon" disabled={isSaving}
                    aria-label={`Remove email address ${index + 1}`}
                    onClick={() => setRecipients(current => current.filter((_, row) => row !== index))}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              {recipients.length === 0 && <p className="text-sm text-gray-400">No recipients. Add an email address to receive lead summaries.</p>}
              <Button type="button" variant="outline" size="sm" disabled={isSaving}
                onClick={() => setRecipients(current => [...current, ''])}>
                <Plus className="w-4 h-4 mr-2" /> Add email
              </Button>
            </>
          ) : savedEmails.length > 0 ? (
            <ul className="space-y-2">
              {savedEmails.map((email, index) => (
                <li key={index} className="break-all text-white py-2 px-3 bg-gray-900 rounded border border-gray-700">{email}</li>
              ))}
            </ul>
          ) : <p className="text-sm text-gray-400">No recipients configured.</p>}
          {error && <p id="leads-email-error" role="alert" className="text-sm text-red-400">{error}</p>}
        </CardContent>
      </form>
    </Card>
  );
};

export default LeadsEmailSection;
